<style></style>

<template>
    <div class="page-container" role="tablist">
        <h1>Step 5: Keep It Up!</h1>

        <section-collapse accordion="step-5-keep-it-up" id="step-5-keep-it-up-why-important">
            <div slot="title">Why it's Important</div>
            <div slot="collapsed-content">
                <p>Child care providers finish a cycle of Go NAPSACC by repeating the self-assessment. Updated results help them reflect on how far they have come and where they might go next. Ideally, child care providers continue with more cycles of Go NAPSACC even without the same level of support. Praising their progress and planning how to keep up the work supports their continuous quality improvement. Over time, repeated self-assessments help show the impact of your work, which is important for continued success and funding.</p>
            </div>
        </section-collapse>

        <section-collapse accordion="step-5-keep-it-up" id="step-5-keep-it-up-activities">
            <div slot="title">
                Activities
            </div>
            <div slot="collapsed-content">
                <p>Click any activity to read more</p>

                <activity-collapse accordion="step-5-activities">
                    <div slot="number">1</div>
                    <div slot="title"><strong>Child care providers</strong> take the self-assessment again</div>
                    <div slot="collapsed-content">
                        <div>
                            <p>Child care providers retake self-assessments in the same modules to finish a cycle of Go NAPSACC. Length of a cycle can be defined many ways – once 5 or more goals are complete, after 6-12 months of participation, or some other schedule set by your state. Some states link incentives with retaking self-assessments.</p>
                        </div>
                        <provider-tools-spotlight class="mt-4">
                            <slot>
                                <h4>To retake a self-assessment…</h4>
                                <ol class="list-left-aligned">
                                    <li>Direct the child care provider to their My NAPSACC page. </li>
                                    <li>Under the My Activities section, start a new self-assessment by clicking on the Start New Assessment button beside Assess (step 1) or the Retake My Assessment button beside Keep It Up (step 5).</li>
                                </ol>
                                <img :src="require('public@/images/ta-resources/5-keep-it-up/19 Step5KeepItUp-Provider-To retake a self-assessment.png')" class="w-100 mb-4" style="margin-left: -12px">
                            </slot>
                        </provider-tools-spotlight>
                    </div>
                </activity-collapse>

                <activity-collapse accordion="step-5-activities">
                    <div slot="number">2</div>
                    <div slot="title"><strong>Consultant</strong> provides help and support</div>
                    <div slot="collapsed-content">
                        <div>
                            <p>After reaching their goals, child care providers may see their work as done. It’s important to make sure that they completed all action plan steps and retake the self-assessment. Send a quick email or text reminder about your upcoming check-in and their self-assessment deadline.</p>
                        </div>

                        <h5>Check-in Prep:</h5>
                        <ul>
                            <li>Use your Consultant Tools to monitor child care providers’ activities. See if they have marked any more action plan steps as complete or goals as reached.</li>
                            <li>See if they have retaken the self-assessments. If so, compare the old and new results to see where scores improved.</li>
                            <li>Use the Add TA Activity button to record notes for easy reference during the check-in. Mark the Activity Type as Planning Notes.</li>
                            <li>Review the agenda included below and make any needed changes or notes.</li>
                        </ul>
                        <consultant-tools-spotlight class="mt-4">
                            <slot>
                                <h4>To monitor progress...</h4>
                                <ol class="list-left-aligned">
                                    <li>Go to your Reports page.</li>
                                    <li>Run a Detailed Activity report.</li>
                                    <li>Once the report is compiled, it will appear in your list of Last 10 reports.</li>
                                    <li>Open the report for detailed information about each program, including first and last self-assessment scores and the number of goals selected and completed for each module.</li>
                                </ol>
                                <img :src="require('public@/images/ta-resources/5-keep-it-up/20 Step5KeepItUp-Consultant-To monitor progress.png')" class="w-100 mb-4" style="margin-left: -12px">
                            </slot>
                        </consultant-tools-spotlight>
                        <h5>Check-in Agenda:</h5>
                            <strong class="text-primary">Inquire about progress</strong>
                            <ul class="fa-ul">
                                <li>“How are those final steps going? Have you updated the parent handbook, staff manual, and policies?”</li>
                            </ul>

                            <strong class="text-primary">If child care providers come across challenges that prevent them from reaching goals</strong>
                            <ul class="fa-ul">
                                <li>Talk through challenges and brainstorm strategies for overcoming them. Remember that flexibility is important.</li>
                                <li>Find helpful resources in the tips & materials library.</li>
                                <li>Discuss other steps to be taken and what should come next.</li>
                                <li>Together, choose a new target date for completion.</li>
                            </ul>

                            <strong class="text-primary">Encourage reflection about process and sustainability</strong>
                            <p>Ask questions about specific goals and changes made.</p>
                            <ul class="fa-ul">
                                <li>“How are you feeling about the changes you’ve made? How are teachers/staff/families/children responding to the progress? What challenges did you have reaching this goal that you weren’t expecting? Is there anything you wish you’d done differently along the way?”</li>
                                <li>“What will help you keep up with [goal/change] over the months and years to come?”</li>
                            </ul>

                            <strong class="text-primary">Praise progress and discuss results of their most recent self-assessment</strong>
                            <p>Use the progress bar at the top of their My NAPSACC page to show how self-assessments capture improvements.</p>
                            <ul class="fa-ul">
                                <li>“You have made clear progress! Your self-assessment shows that you are now meeting best practices for…”</li>
                            </ul>

                            <strong class="text-primary">Encourage reflection about accomplishments and future goals</strong>
                            <p>If any goals are still open, assess interest in continued work on those goals.</p>
                            <ul class="fa-ul">
                                <li>“How do you feel about your results?”</li>
                                <li>“What stood out to you?”</li>
                                <li>“Which goals are you thinking about working on next?”</li>
                            </ul>

                            <strong class="text-primary">Discuss next steps</strong>
                            <ul class="fa-ul">
                                <li>Set a deadline for selecting goals for their next cycle of Go NAPSACC.</li>
                                <li>Set a date for an in-person check-in to begin action planning.</li>
                            </ul>

                        <div class='mt-5 mb-5'>
                            <p><strong>After the check-in</strong>, document your interaction and any notes using the Add TA Activity button.</p>
                        </div>

                        <helpful-hints>
                            
                            <h5>Document progress</h5>
                            <p>Encourage child care providers to make a display with pictures, stories, or other visuals onto a poster or bulletin board to show improvements. These displays remind child care providers of their accomplishments and allow them to share their success stories with their families.</p>

                            <h5>Recognize and celebrate success</h5>
                            <p>Share success stories through social media, newsletters, or other media. Your public recognition of their work builds a sense of pride. Include these stories in your organization’s annual reports and Go NAPSACC outreach materials used to expand this work. Make sure you have the proper authorizations and photo releases before sharing stories and photos.</p>

                            <h5>Stay in touch</h5>
                            <p>Even if you do not have funding to support another cycle of Go NAPSACC, be sure to keep in touch with child care providers. Some ideas include:</p>
                            <ul>
                                <li>Remind them that they can still access Go NAPSACC and set goals, create action plans, and access tips & materials. They have the skills to navigate Go NAPSACC and continue making healthy changes.</li>
                                <li>Send monthly emails, newsletters, social media posts, or text prompts to all current and past participants about health-related topics (for example, indoor activities during winter, gardening in spring).</li>
                                <li>Share continuing education on health-related quality improvement available from your organization. Let them know about online trainings and local trainings you are providing.</li>
                                <li>Invite past participants to mentor new ones. They can provide a wealth of knowledge and real-world experience about overcoming challenges and reaching goals.</li>
                                <li>Consider organizing a reunion so past participants can come together, share challenges and successes, and support ongoing growth. Be sure to set the date well in advance.</li>
                            </ul>
                                
                        </helpful-hints>
                    </div>
                </activity-collapse>

            </div>
        </section-collapse>

        <section-collapse accordion="step-5-keep-it-up" id="step-5-keep-it-up-challenges-and-solutions">
            <div slot="title">
                Common Challenges &amp; Solutions
            </div>
            <div slot="collapsed-content">
                <h4 class="mt-0">Child care providers are not retaking the self-assessments</h4>
                <p>Make sure to set a clear deadline for retaking the self-assessments. It can also be helpful to remind them how to retake self-assessments.</p>
                <p>Remind them of the value of retaking the self-assessments. In addition to capturing their personal success, these self-assessments help you demonstrate the impact of the Go NAPSACC program so that it can continue. Further, Go NAPSACC encourages continuous quality improvement. While they have made progress toward several goals, there is likely still room for continued improvements.</p>
                <p>Decide what type of follow-up is most effective. You may start with email and text messages, then move on to phone calls and possibly a drop-in visit. Ask your local or state-level organization about what amount of follow-up is enough and at what point it is okay to stop.</p>


                <h4 class="mt-0">Decreases in self-assessment scores</h4>
                <p>Self-assessment scores may decrease. Sometimes child care providers don’t fully understand questions when they take the self-assessment the first time and that can lead to their scores go down even if practices have improved. If you notice a decrease in score, ask them if they interpreted any questions differently over time.</p>
                <p>Scores may also decrease because practices decline. While a child care provider is focused on reaching goals in one area, other things may slip. Assure them that small slips happen, but they can become easy goals to include in the next cycle to get quickly back on track.</p>
            </div>
        </section-collapse>

        <section-collapse accordion="step-5-keep-it-up" id="step-5-keep-it-up-support-documents">
            <div slot="title">
                Support Documents
            </div>
            <div slot="collapsed-content">
                <ul class="px-3 support-docs-list">
                    <li><strong><a :href="$basePath+'/uploads/navigation_of_provider_tools.pdf'" target="_blank" class="text-warning">Navigation of Provider Tools</a></strong></li>
                    <li><strong><a :href="$basePath+'/uploads/navigation_of_consultant_tools.pdf'" target="_blank" class="text-warning">Navigation of Consultant Tools</a></strong></li>
                    <li><strong><a :href="$basePath+'/uploads/remote_support_strategies.pdf'" target="_blank" class="text-warning">Remote Support Strategies</a></strong></li>
                    <li><strong><a :href="$basePath+'/uploads/consultant-resources/implementing_go_napsaccc_in_spanish.pdf'" target="_blank" class="text-warning">Implementing Go NAPSACC in Spanish</a></strong></li>
                    <li><strong><a :href="$basePath+'/uploads/consultant-resources/go_napsacc_consultant_webinar_recordings.pdf'" target="_blank" class="text-warning">Go NAPSACC Consultant Webinar Recordings</a></strong></li>
                    <li><strong><a :href="$basePath + '/uploads/consultant-resources/continuum_of_support.pdf'" target=
                        "_blank" class="text-warning">Continuum of Support</a></strong></li>
                </ul>
            </div>
        </section-collapse>
    </div>
</template>

<script>
    import ConsultantToolsSpotlight from "../ConsultantToolsSpotlight";
    import ProviderToolsSpotlight from "../ProviderToolsSpotlight";
    export default {
        components: {
            ConsultantToolsSpotlight,
            ProviderToolsSpotlight
        },
        data() {
            return {}
        },
        methods: {},
        computed: {
            $basePath() {
              return window.basePath;
            }
        }
    
}
</script>